/**
 * Cookie Consent Types
 *
 * Defines the data model for granular cookie/storage consent management.
 * Categories follow the standard privacy model: Essential (always on),
 * Functional (optional UX), and Analytics (optional tracking).
 */

/**
 * Consent categories used across all apps.
 * - essential: Required for the site to function. Always enabled.
 * - functional: Optional UX features (theme, preferences, unlock state).
 * - analytics: Optional performance tracking (Vercel Analytics).
 */
export type ConsentCategory = 'essential' | 'functional' | 'analytics';

/**
 * App scope used for category metadata overrides.
 * '*' applies to all apps unless an app-specific override exists.
 */
export type ConsentApp =
  | '*'
  | 'home'
  | 'trueid-locker'
  | 'company'
  | 'admin'
  | 'xanectech';

/**
 * Source of a consent decision — used for auditing and migration tracking.
 */
export type ConsentSource =
  | 'accept-all'
  | 'reject-non-essential'
  | 'settings'
  | 'migrated';

/**
 * Per-category consent flags.
 * Essential is always true; functional and analytics are opt-in.
 */
export interface ConsentPreferences {
  essential: true;
  functional: boolean;
  analytics: boolean;
}

/**
 * Full consent state stored in browser localStorage.
 */
export interface ConsentState {
  /** Policy version — bump when category purposes or storage mappings change. */
  version: number;
  preferences: ConsentPreferences;
  source: ConsentSource;
  /** ISO 8601 timestamp of when the decision was made. */
  decidedAt: string;
}

/**
 * Category metadata displayed in the Cookie Settings dialog.
 */
export interface ConsentCategoryMeta {
  app: ConsentApp;
  id: ConsentCategory;
  label: string;
  description: string;
  /** Examples of what storage is used for this category. */
  storageUsage: string[];
  required: boolean;
}

/**
 * Canonical category metadata for v1 scope.
 * Marketing is excluded from v1 — the model is extensible for future phases.
 */
export const CONSENT_CATEGORIES: ConsentCategoryMeta[] = [
  {
    app: '*',
    id: 'essential',
    label: 'Strictly Necessary',
    description:
      'Required for the website to function. Cannot be disabled. Includes authentication, session security, and encrypted key storage.',
    storageUsage: [
      'Authentication tokens (cookies)',
      'Encrypted session keys (IndexedDB)',
      'Encryption key material (IndexedDB)',
    ],
    required: true,
  },
  {
    app: 'trueid-locker',
    id: 'functional',
    label: 'Functional',
    description:
      'Enables optional features that improve your experience, such as remembering your theme preference and unlock duration. Disabling these means preferences will not be saved across sessions.',
    storageUsage: [
      'Theme preference (localStorage / IndexedDB)',
      'Unlock duration preference (IndexedDB)',
      'User interface settings (localStorage)',
    ],
    required: false,
  },
  {
    app: '*',
    id: 'functional',
    label: 'Functional',
    description:
      'Enables optional features that improve your experience, such as remembering your theme preference. Disabling these means preferences will not be saved across sessions.',
    storageUsage: ['Theme preference (localStorage)'],
    required: false,
  },
  {
    app: '*',
    id: 'analytics',
    label: 'Analytics',
    description:
      'Collects anonymised performance data to help improve the application. Uses Vercel Analytics and Speed Insights. No personal data is sent.',
    storageUsage: [
      'Vercel Analytics page-view events',
      'Vercel Speed Insights performance metrics',
    ],
    required: false,
  },
];

const CONSENT_CATEGORY_ORDER: ConsentCategory[] = [
  'essential',
  'functional',
  'analytics',
];

/**
 * Resolve category metadata for an app with wildcard fallback.
 */
export function getConsentCategoriesForApp(
  appName: string,
): ConsentCategoryMeta[] {
  const normalizedApp = appName.toLowerCase();

  return CONSENT_CATEGORY_ORDER.flatMap((categoryId) => {
    const appSpecific = CONSENT_CATEGORIES.find(
      (category) =>
        category.app === normalizedApp && category.id === categoryId,
    );

    if (appSpecific) {
      return [appSpecific];
    }

    const wildcard = CONSENT_CATEGORIES.find(
      (category) => category.app === '*' && category.id === categoryId,
    );

    return wildcard ? [wildcard] : [];
  });
}
